import { useEffect, useState, useRef } from 'react';
import InputBox from '../../components/InputBox';
import Accordion from 'react-bootstrap/Accordion';
import { getTempUserDetails, getFeedbackFieldNames, submitResubmit } from '../../services/users/tempUserDetails';
import { getGlobalOptions } from '../../services/auth/selfSignUp';
import { convertForSelect } from '../../utils/convertApiData';
import { enumGlobalOptionData, enumGlobalOptionDataMap } from '../../utils/enumGlobalOptionData';
import { selfSignUpUserData } from '../../utils/selfSignUpUserData';
import { toast } from '../../components/AppToast';

const dropdownFields = [
  'tenant_industryType',
  'tenant_RegDocuments',
  'tenant_type',
  'tenant_title',
  'tenant_country',
  'tenant_subscriptionModel',
];
const checkboxFields = ['tenant_systemCapabilities'];

const TempUserDetails = () => {
  const [formData, setFormData] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [checkboxOptions, setCheckboxOptions] = useState({});
  const [docTypeOptions, setDocTypeOptions] = useState([]); // For DOC_TYPE dropdown
  // State for new document upload
  const [newDoc, setNewDoc] = useState({ doc_type: '', file: null, name: '' }); // For adding new document
  const fileInputRef = useRef(null);
  const [editableFieldNames, setEditableFieldNames] = useState([]);
  const [originalData, setOriginalData] = useState({});

  const getDropdownData = async (key) => {
    try {
      const data = await getGlobalOptions(enumGlobalOptionData?.[key]);
      return convertForSelect(data);
    } catch (err) {
      toast.error(err);
      return [];
    }
  };

  const fetchAllDropdownOptions = async () => {
    const ddOptions = {};
    const cbOptions = {};
    const exceptKeys = ['tenant_country', 'tenant_subscriptionModel'];
    for (const field of dropdownFields) {
      if (!exceptKeys.includes(field)) {
        let enumKey = enumGlobalOptionDataMap[field];
        ddOptions[field] = await getDropdownData(enumKey);
      }
    }
    for (const field of checkboxFields) {
      let enumKey = enumGlobalOptionDataMap[field];
      cbOptions[field] = await getDropdownData(enumKey);
    }
    setCheckboxOptions(cbOptions);
    setDropdownOptions(ddOptions);
  };

 const fetchDocTypes = async()=>{
      let enumKey = enumGlobalOptionDataMap["tenant_RegDocuments"];
      const options = await getDropdownData(enumKey);
      setDocTypeOptions(options);
  }

  const fetchEditableFields = async () => {
      try {
        // You may want to get tenantId and token dynamically
        const tenantId = formData?._id;
        const fields = await getFeedbackFieldNames(tenantId);
        setEditableFieldNames(fields);
      } catch (err) {
        // fallback or error
        setEditableFieldNames([]);
      }
    };

  useEffect(() => {
    fetchAllDropdownOptions();
    getUserData().then(() => {fetchEditableFields();});
    fetchDocTypes();
    // eslint-disable-next-line
  }, []);

  const getUserData = async () => {
    try {
      const data = await getTempUserDetails();
      // Merge address fields into formData for flat access
      const flatData = {
        ...data,
        ...(data.address || {})
      };
      setFormData(flatData);
      setOriginalData(flatData); // Save original for diff
    } catch (err) {
      console.log(err, 'error');
    }
  };

  const handleChange = (section, field, value, idx) => {
    if (section === 'contact') {
      const updatedContact = [...(formData.contact || [])];
      updatedContact[idx][field] = value;
      setFormData({ ...formData, contact: updatedContact });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async () => {
    const submittedData = {};
    editableFieldNames.forEach(field => {
      if (formData[field] !== originalData[field]) {
        submittedData[field] = formData[field];
      }
    });
    if (!formData._id) {
      toast.error('Missing tenantId');
      return;
    }
    try {
      await submitResubmit(formData._id, submittedData);
      toast.success('Resubmitted successfully!');
    } catch (err) {
      toast.error('Error resubmitting: ' + (err?.message || err));
    }
  };

  const renderFields = (fields, section = null, idx = null) => {
    return Object.keys(fields).map((key) => {
      if (
        Array.isArray(fields[key]) &&
        key !== 'contact' &&
        key !== 'tenant_regDetails' &&
        !checkboxFields.includes(key) &&
        !dropdownFields.includes(key)
      ) return null;
      if (typeof fields[key] === 'object' && fields[key] !== null && !Array.isArray(fields[key])) return null;
      if (key === 'contact') return null;
      let isEditable = editableFieldNames.includes(key);
      if (section === 'contact' && idx !== null) {
        // Check for keys like 'tenant_title_1', 'tenant_title_2', etc.
        const indexedKey = `${key}_${idx + 1}`;
        isEditable = editableFieldNames.includes(indexedKey);
      }
      const label = section === 'contact' && selfSignUpUserData.contact
        ? selfSignUpUserData.contact[Object.keys(selfSignUpUserData.contact).find(k => k.toLowerCase() === key.toLowerCase())] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        : (selfSignUpUserData[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      // Dynamic column size for dropdowns
      let dropdownColClass = 'col-md-3';
       if (['tenant_title'].includes(key)) {
        dropdownColClass = 'col-md-2';
      }
      if (dropdownFields.includes(key)) {
        return (
          <div className={dropdownColClass} key={key}>
            <label className="form-label fw-semibold small">
              {label}
              {isEditable && <span className="required">*</span>}
            </label>
            <select
              className="form-control"
              value={fields[key] || ''}
              onChange={isEditable ? (e => handleChange(section, key, e.target.value, idx)) : undefined}
              disabled={!isEditable}
              required={isEditable}
            >
              <option value="">Select</option>
              {(dropdownOptions[key] || []).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );
      }
      if (checkboxFields.includes(key)) {
        return (
          <div className="field-row" key={key} style={{ width: '100%' }}>
            <label className="form-label fw-semibold small mb-2">
              {label}
              {isEditable && <span className="required">*</span>}
            </label>
            <div className="d-flex flex-wrap flex-column">
              {(checkboxOptions[key] || []).map(opt => (
                <div key={opt.value} className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`${key}_${opt.value}`}
                    value={opt.value}
                    checked={Array.isArray(fields[key]) && fields[key].includes(opt.value)}
                    onChange={isEditable ? (e => {
                      let newValue = Array.isArray(fields[key]) ? [...fields[key]] : [];
                      if (e.target.checked) {
                        newValue.push(opt.value);
                      } else {
                        newValue = newValue.filter(v => v !== opt.value);
                      }
                      handleChange(section, key, newValue, idx);
                    }) : undefined}
                    disabled={!isEditable}
                  />
                  <label className="form-check-label" htmlFor={`${key}_${opt.value}`}>{opt.label}</label>
                </div>
              ))}
            </div>
          </div>
        );
      }
      // Render single checkbox for boolean fields
      if (typeof fields[key] === 'boolean') {
        return (
          <div className="col-md-12 d-flex align-items-center" key={key}>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${key}_checkbox_${idx ?? ''}`}
                checked={fields[key]}
                onChange={isEditable ? (e => handleChange(section, key, e.target.checked, idx)) : undefined}
                disabled={!isEditable}
              />
              <label className="input-label d-block ms-2" htmlFor={`${key}_checkbox_${idx ?? ''}`}>{label}</label>
            </div>
          </div>
        );
      }
      // Handle tenant_regDetails as a special case in renderFields
      if (key === 'tenant_regDetails' && Array.isArray(fields[key])) {
        const editMode = editableFieldNames.includes('tenant_regDetails_doc_type_1');
        return (
          <div className="col-12" key={key}>
            {editMode && (
              <div className="row mb-3 gap-4">
                <div className="col-md-4">
                  <label className="form-label">Select Document Type</label>
                  <select
                    className="form-control"
                    value={newDoc.doc_type || ''}
                    onChange={e => setNewDoc({ ...newDoc, doc_type: e.target.value })}
                  >
                    <option value="">Select</option>
                    {docTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Select Document File</label>
                  <input
                    type="file"
                    className="form-control"
                    accept={
                      newDoc.doc_type === 'image' ? 'image/*' :
                      newDoc.doc_type === 'pdf' ? 'application/pdf' :
                      newDoc.doc_type === 'doc' ? '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' : '*'
                    }
                    ref={fileInputRef}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = ev => {
                          setNewDoc({ ...newDoc, file: ev.target.result, name: file.name });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      if (newDoc.doc_type && newDoc.file && newDoc.name) {
                        const updatedDocs = Array.isArray(fields[key]) ? [...fields[key], { ...newDoc }] : [{ ...newDoc }];
                        handleChange(null, key, updatedDocs);
                        setNewDoc({ doc_type: '', file: null, name: '' });
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      } else {
                        toast.error('Please select document type and file.');
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
            {/* Uploaded Documents List */}
            <div className="row mb-3 gap-4">
              <div className="col-md-4 mb-3" key={idx}>
                <h6>Uploaded Documents</h6>
                <ul>
                  {fields[key]?.map((doc, idx2) => (
                    <li key={idx2} className="d-flex align-items-center gap-2">
                      <span>{doc.name} ({doc.doc_type})</span>
                      {editableFieldNames.includes('tenant_regDetails_doc_type_1') && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            const updatedDocs = fields[key].filter((_, i) => i !== idx2);
                            handleChange(null, key, updatedDocs);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      }
      return (
        <InputBox
          key={key}
          label={label}
          value={fields[key]}
          onChange={isEditable ? (e) => handleChange(section, key, e.target.value, idx) : undefined}
          readOnly={!isEditable}
          isRequired={isEditable}
          labelClassName="input-label"
        />
      );
    });
  };

  return (
    <div>
      <h4 className="mb-4 text-center fw-bold">User Details</h4>
      <div className="temp-user-details black-white-theme container mt-4 p-4 rounded shadow">
        <Accordion defaultActiveKey="-1" alwaysOpen className="custom-accordion">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Welcome</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_type: formData.tenant_type })}
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>Identification</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_name: formData.tenant_name, tenant_industryType: formData.tenant_industryType ,tenant_regDetails:formData.tenant_regDetails })}
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>Address</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_address1: formData.tenant_address1, tenant_city: formData.tenant_city, tenant_state: formData.tenant_state, tenant_postCode: formData.tenant_postCode, tenant_country: formData.tenant_country })}
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="3">
            <Accordion.Header>Contact</Accordion.Header>
            <Accordion.Body>
              {formData?.contact?.map((contact, idx) => (
                <div className='container-fluid' key={idx}>
                  <div className="row mb-3 gap-4">
                    <h5 className="col-md-12 fw-bold">Contact {idx + 1}</h5>
                    {renderFields(contact, 'contact', idx)}
                    <div className="col-md-12"></div>
                  </div>
                </div>
              ))}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="4">
            <Accordion.Header>System Information</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_wmsSystemInUse: formData.tenant_wmsSystemInUse, tenant_erpSystemInUse: formData.tenant_erpSystemInUse, tenant_integrationRequired: formData.tenant_integrationRequired, tenant_numberOfUserLicenses: formData.tenant_numberOfUserLicenses })}
                {renderFields({ tenant_systemCapabilities: formData.tenant_systemCapabilities })}
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="5">
            <Accordion.Header>Payment & Submit</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_subscriptionModel: formData.tenant_subscriptionModel })}
                {renderFields({ tenant_paymentDetails: formData.tenant_paymentDetails })}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        <button disabled={editableFieldNames.length===0} onClick={handleSubmit} className="btn btn-secondary mt-3">Submit</button>
      </div>
    </div>
  );
};

export default TempUserDetails;
